import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ReactiveFormsModule, UntypedFormControl} from '@angular/forms';
import {Observable, of, Subject} from 'rxjs';
import {Company} from '../../opportunities/interfaces/company.interface';
import {debounceTime, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {DataService} from '../../services/data.service';
import {ConfigService} from '../../services/config.service';
import {MatFormField, MatInput, MatLabel} from '@angular/material/input';
import {MatAutocomplete, MatAutocompleteTrigger, MatOption} from '@angular/material/autocomplete';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {AsyncPipe} from '@angular/common';



@Component({
    selector: 'app-company-search',
    imports: [
        MatFormField,
        MatLabel,
        MatAutocomplete,
        MatProgressSpinner,
        MatOption,
        ReactiveFormsModule,
        MatAutocompleteTrigger,
        MatInput,
        AsyncPipe
    ],
    template: `
        <mat-form-field appearance="outline" style="width: 100%">
            <mat-label>Company name</mat-label>
            <input matInput [formControl]="formControl_" [required]="required" [matAutocomplete]="autocomplete"/>

            <mat-autocomplete #autocomplete="matAutocomplete" [displayWith]="companyDisplayFn">
                @for (c of (companyOptions$ | async); track c) {
                    <mat-option [value]="c">
                        {{ c.name }}
                    </mat-option>
                }
            </mat-autocomplete>

            @if (searching) {
                <ng-container matSuffix>
                    <mat-progress-spinner mode="indeterminate" strokeWidth="2" diameter="20"></mat-progress-spinner>
                </ng-container>
            }
        </mat-form-field>`
})
export class CompanySearchComponent implements OnInit, OnDestroy {
    @Input() formControl_: UntypedFormControl;
    @Input() required = false;
    @Output() companySelection: EventEmitter<Company | null> = new EventEmitter<Company | null>();
    @Output() companyValidityChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    private unsubscribe$: Subject<void> = new Subject<void>();
    searching = false;

    companyOptions$: Observable<any[]>;

    constructor(
        private dataService: DataService,
        private configService: ConfigService
    ) {
    }

    ngOnInit() {
        this.formControl_.setValidators(this.companyValidator);
        this.companyOptions$ = this.formControl_.valueChanges.pipe(
            debounceTime(200),
            switchMap(v => {
                if (typeof v === 'string' && v.trim().length) {
                    this.searching = true;
                    this.companySelection.emit(null);
                    return this.dataService.searchCompany(v.trim()).pipe(
                        map(res => (res['results'] as unknown) as any[] || [])
                    );
                }

                const cosmoKey = (this.configService.settings.value.value.prosperworks as any)['cfCosmoId'];
                const cosmoId = cosmoKey ?
                    v?.custom_fields?.find((f: any) => f.custom_field_definition_id === parseInt(cosmoKey, 0)).value : null;
                this.companySelection.emit({...v, cosmoId: cosmoId});
                return of([]);
            }),
            tap(() => this.searching = false)
        );

        this.formControl_.statusChanges.pipe(
            takeUntil(this.unsubscribe$),
            map(v => v === 'VALID')
        ).subscribe(this.companyValidityChange);
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    companyDisplayFn(value: Company) {
        return value?.name || '';
    }

    private companyValidator = (control) => {
        return (typeof control.value === 'object') ? null : {'required': true};
    }
}
